import React, { useState } from 'react';

// Third Party Libraries
import moment from 'moment';

// Hooks
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
import { cancelOrder, payAndProcess } from '~/redux/actions/orders-actions';
import { getUserData } from '~/redux/actions/auth-actions.js';

// Utils
import { dateFormat, getTimeOnly } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { createViewProofForOrder } from '~/utils/template-builder';

// Components
import ConfirmDialog from '~/components/Orders/ConfirmDialog/ConfirmDialog';
import AddFunds from '~/components/Orders/AddFunds';

// Mui Components
import {
  Grid,
  CircularProgress,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import { ProcessOrderCIcon, CancelOrderCIcon, InsufficientCIcon } from '~/assets/images';

// styles
import './styles.scss';

// Custom MUI Stepper Components for HoldStepper
const HoldCustomConnector = styled(StepConnector)(({ theme, ownerState }) => ({
  '&.MuiStepConnector-alternativeLabel': {
    top: 8,
    left: 'calc(-50% + 8px)',
    right: 'calc(50% + 8px)',
  },
  '& .MuiStepConnector-line': {
    height: 2,
    border: 0,
    backgroundColor: ownerState?.index > 0 ? '#ed5c2f' : '#e5e5e5',
    borderRadius: 1,
  },
  '&.MuiStepConnector-active .MuiStepConnector-line': {
    backgroundColor: '#ed5c2f',
  },
  '&.MuiStepConnector-completed .MuiStepConnector-line': {
    backgroundColor: '#ed5c2f',
  },
}));

const HoldCustomStepIcon = styled('div')(({ ownerState }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: ownerState.active || ownerState.completed ? '#ed5c2f' : '#e5e5e5',
  transition: 'all 0.3s ease',
}));

function HoldStepper({ info, overview, refreshOverview, loading, orderCost }) {
  const [activeStep, setActiveStep] = useState(1);

  const [proofLoading, setProofLoading] = useState(false);

  const [fundsModal, setFundsModal] = useState({ open: false, params: null });

  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    loading: false,
    currentAction: null,
    params: null,
  });

  const { id } = useParams();
  const dispatch = useDispatch();

  const downloadViewProof = async () => {
    try {
      setProofLoading(true);
      await dispatch(
        createViewProofForOrder(`Order-${id}`, {
          orderId: id,
        })
      );
      setProofLoading(false);
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      setProofLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialog({ open: false, message: '', loading: false, params: null, currentAction: null });
  };

  const handleOk = async () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.currentAction === 'cancel') {
      const payload = { orderId: id };
      const response = await dispatch(cancelOrder(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        refreshOverview();
      } else {
        dispatch(failure(response.data.message));
      }
    } else if (dialog.currentAction === 'payAndProcess') {
      const payload = { orderId: id };
      const response = await dispatch(payAndProcess(payload));
      if (response.status === 200) {
        dispatch(getUserData());
        dispatch(success(response.data.message));
        refreshOverview();
      } else {
        if (
          response.data.message === 'Auto Funding Failed' ||
          response.data.message == 'Auto Funding is disabled'
        ) {
          setTimeout(() => {
            handleInsufficientFunds(dialog.params);
          }, 1000);
        } else {
          dispatch(failure(response.data.message));
        }
      }
    } else if (dialog.currentAction == 'addFunds') {
      setFundsModal({ open: true, params: dialog.params });
    }
    setDialog((prev) => ({ ...prev, loading: false, open: false }));
  };

  const handleCancelOrder = async () => {
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MESSAGE,
      loading: false,
      currentAction: 'cancel',
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUB_MESSAGE,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
      icon: CancelOrderCIcon,
    });
  };

  const handlePayAndProcessOrder = async () => {
    let currentDate = moment();
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MESSAGE,
      loading: false,
      currentAction: 'payAndProcess',
      params: { id, cost: orderCost },
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUCCESS_BUTTON,
      subMessage: `${MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUB_MESSAGE} ${dateFormat(currentDate, true)}`,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.HEADING,
      icon: ProcessOrderCIcon,
    });
  };

  const handleInsufficientFunds = (params) => {
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.MESSAGE,
      loading: false,
      currentAction: 'addFunds',
      params: params || { id, cost: orderCost },
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUB_MESSAGE,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.HEADING,
      icon: InsufficientCIcon,
    });
  };

  const steps = [
    {
      label: 'Order Date',
      date: overview?.orderDates?.createdDateDisplay || `${dateFormat(info.createdAt, true)}`,
    },
    {
      label: info.status,
      subText: info.paymentStatus,
      date: `${dateFormat(info.updatedAt, true)}`,
      time: getTimeOnly(info.updatedAt),
    },
  ];

  const HoldCustomStepIconComponent = (props) => {
    const { active, completed } = props;
    return <HoldCustomStepIcon ownerState={{ active, completed }} />;
  };

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={50} />;
  }

  return (
    <Box className="hold_stepper_wrapper" sx={{ width: '100%' }}>
      <Grid container>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Box className="main_layer">
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<HoldCustomConnector ownerState={{ index: activeStep > 0 }} />}
              className="hold-custom-mui-stepper"
            >
              {steps.map((step, index) => (
                <Step key={step.label} completed={index < activeStep}>
                  <StepLabel StepIconComponent={HoldCustomStepIconComponent}>
                    <Box className="step-content">
                      <Box className="step-text-top">{step.label}</Box>
                      {step.subText && <Box className="step-subtext-top">{step.subText}</Box>}
                      <Box className="bottomtxt">{step.date}</Box>
                      {step.time && <Box className="bottomtxt">{step.time}</Box>}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>

        {/* <Grid item lg={12} md={12} sm={12} xs={12}>
          {info.status !== 'Canceled' && (
            <div className="btn_outer">
              <Button onClick={() => handleCancelOrder()}>
                {MESSAGES.ORDERS.DETAIL.OVERVIEW.CANCEL_ORDER_BUTTON}
              </Button>
              {proofLoading ? (
                <Button disabled style={{ width: 97 }}>
                  <CircularProgress
                    sx={{
                      color: '#ED5C2F',
                      width: '20px !important',
                      height: '20px !important',
                    }}
                  />
                </Button>
              ) : (
                <Button onClick={() => downloadViewProof()}>
                  {MESSAGES.ORDERS.DETAIL.OVERVIEW.VIEW_PROOF_BUTTON}
                </Button>
              )}
              <Button onClick={() => handlePayAndProcessOrder()} className="activeBtn">
                {MESSAGES.ORDERS.DETAIL.OVERVIEW.PAY_AND_PROCESS_BUTTON}
              </Button>
            </div>
          )}
        </Grid> */}
      </Grid>
      {dialog.open && (
        <ConfirmDialog
          dialog={dialog}
          open={dialog.open}
          loading={dialog.loading}
          handleClose={handleCloseDialog}
          handleOk={handleOk}
        />
      )}
      {fundsModal.open && (
        <AddFunds
          params={fundsModal.params}
          open={fundsModal.open}
          refreshOverview={refreshOverview}
          handleOpen={() => {
            setFundsModal((prev) => ({ ...prev, open: true }));
          }}
          handleClose={() => {
            setFundsModal((prev) => ({ ...prev, open: false }));
          }}
        />
      )}
    </Box>
  );
}
export default HoldStepper;
